import styled from '@emotion/styled';
import {useAtomValue} from 'jotai';
import {observer} from 'mobx-react-lite';
import React, {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  EAutocomplete,
  EForm,
  ESmallButton,
  ETextArea,
} from 'webapp-lib/Components';

import {ActionButtons, Loading} from '#';
import notificationsApi, {
  TaskDueDateReachedNotification,
  TaskEntity,
  TaskResponse,
} from '@lexian/api/notifications-api';
import {Task} from '_/assets/images';
import {useRouteNavigate} from '_/hooks/useRouteNavigate';
import {I18NextNS} from '_/i18n';
import {featureFlagState} from '_/state/featureFlags';
import {State} from '_/utilities';
import {EFDateInput, useForm} from '_/utilities/form';
import {Loadable} from '_/utilities/types';

import {StoreContext} from '../../store';

import {ExclamationMark, FormHeader, HeaderRow, TextWrapper} from './styles';

export type Props = {
  notification: TaskDueDateReachedNotification;
};

enum TaskDueDateReachedFormFields {
  Date = 'date',
}

const isTaskResponse = (task: unknown): task is TaskResponse =>
  Boolean((task as TaskResponse).id);

function UTaskDueDateReached({notification}: Props): React.ReactElement {
  return (
    <div>
      <HeaderRow>
        <Task />
        <ExclamationMark show={notification.importantFlag} />
        <TextWrapper>{notification.message}</TextWrapper>
      </HeaderRow>
      <TaskDueDateReachedContent notification={notification} />
    </div>
  );
}

function TaskDueDateReachedContent({
  notification,
}: Props): React.ReactElement | null {
  const {entity} = notification;
  const [loadable, setLoadable] = useState<Loadable<TaskResponse>>({
    state: State.Loading,
  });
  const [isTaskMissing, setIsTaskMissing] = useState(false);

  useEffect(() => {
    if (!entity) {
      return;
    }
    notificationsApi.getTask({
      parameters: {
        casusId: entity.casusId,
        taskId: entity.id,
      },
      onSuccess: ({body}) => {
        setLoadable(
          isTaskResponse(body)
            ? {
                state: State.Success,
                data: body,
              }
            : {state: State.Error}
        );
      },
      localHandlers: {
        '404': () => {
          setIsTaskMissing(true);
        },
      },
      afterErrorHandler: () => {
        setLoadable({state: State.Error});
      },
    });
  }, [setLoadable, setIsTaskMissing, entity]);

  if (!entity) {
    return null;
  }

  if (isTaskMissing) {
    return (
      <MissingTaskActions>
        <TaskDueDateActions
          entity={entity}
          notification={notification}
          isTaskMissing
        />
      </MissingTaskActions>
    );
  }

  if (loadable.state === State.Error) {
    return null;
  }

  if (loadable.state === State.Loading) {
    return <Loading />;
  }

  return (
    <TaskDueDateForm
      entity={entity}
      notification={notification}
      task={loadable.data!}
    />
  );
}

function TaskDueDateForm({
  notification,
  task,
  entity,
}: {
  notification: TaskDueDateReachedNotification;
  task: TaskResponse;
  entity: TaskEntity;
}): React.ReactElement {
  const store = useContext(StoreContext);
  const {t} = useTranslation<I18NextNS>('notifications');
  const {globalTasks} = useAtomValue(featureFlagState);
  const {handleSubmit, control} = useForm({
    defaultValues: {
      [TaskDueDateReachedFormFields.Date]: task.dueDate,
    },
    onSubmit: (values) => {
      store.resolveTask({
        notificationId: notification.id,
        rescheduleDate: values[TaskDueDateReachedFormFields.Date],
      });
    },
  });

  return (
    <EForm onSubmit={handleSubmit}>
      <FormHeader>{t('taskDueDateReached.taskDetail')}</FormHeader>
      <ETextArea
        label={t('taskDueDateReached.description')}
        value={task.description}
        disabled
      />
      <EFDateInput
        control={control}
        label={t('taskDueDateReached.date')}
        name={TaskDueDateReachedFormFields.Date}
      />
      {!globalTasks ? (
        <EAutocomplete
          label={t('taskDueDateReached.assignee')}
          options={task.assignedUsers.map((it) => ({
            value: it.id,
            displayText: it.displayName,
          }))}
          value={task.assignedUsers[0]?.id}
          disabled
        />
      ) : null}
      <TaskDueDateActions entity={entity} notification={notification} />
    </EForm>
  );
}

function TaskDueDateActions({
  notification,
  entity,
  isTaskMissing,
}: {
  notification: TaskDueDateReachedNotification;
  entity: TaskEntity;
  isTaskMissing?: boolean;
}): React.ReactElement {
  const store = useContext(StoreContext);
  const {t} = useTranslation<I18NextNS>('notifications');
  const {goToCasusTasks} = useRouteNavigate();
  const title = isTaskMissing
    ? t('notificationView.taskNoLongerExists')
    : undefined;

  const completeTask = () => {
    store.resolveTask({notificationId: notification.id});
  };

  const goToSource = () => {
    goToCasusTasks({casusId: entity.casusId, taskId: entity.id});
  };

  return (
    <SplittedActionButtons>
      <ESmallButton disabled={isTaskMissing} title={title} type='submit'>
        {t('taskDueDateReached.reschedule')}
      </ESmallButton>
      <ESmallButton
        disabled={isTaskMissing}
        title={title}
        onClick={completeTask}
      >
        {t('taskDueDateReached.complete')}
      </ESmallButton>
      <ESmallButton disabled={isTaskMissing} title={title} onClick={goToSource}>
        {t('common:actions.goToSource')}
      </ESmallButton>
    </SplittedActionButtons>
  );
}

const MissingTaskActions = styled.div`
  margin-top: var(--gutter);
`;

const SplittedActionButtons = styled(ActionButtons)`
  span:last-of-type {
    margin-left: auto;
  }
`;

export const TaskDueDateReached = observer(UTaskDueDateReached);
